import React, {useEffect, useState} from "react";
import {
    Button,
    CircularProgress,
    ClickAwayListener,
    Fade,
    Grid,
    Popper,
    TextField,
    Typography
} from "@mui/material";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import EnhancedAutocomplete from "../../../components/EnhancedAutocomplete";
import {utilGetSupplierGroups, utilCreateSupplierGroup, utilUpdateSupplierGroup} from "../../../utils/utils";
import {BulkUpdateStatus} from "../../../components/CatalogManagerResults";

interface GroupSupplierPopoverProps {
    anchorEl?: any;
    open: boolean;
    setOpen: (open: boolean) => void;
    partnerIds: (number | string)[];
    onGroup?: () => void;
}

// Group Popover Component (for adding suppliers to groups)
export const GroupSupplierPopover = (props: GroupSupplierPopoverProps) => {
    const {anchorEl, open, setOpen, partnerIds, onGroup} = props;
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState({open: false, errorHeader: "", errorMessage: ""});
    const [availableGroups, setAvailableGroups] = useState<string[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [newGroupName, setNewGroupName] = useState("");

    useEffect(() => {
        const loadGroups = async () => {
            try {
                const groups = await utilGetSupplierGroups();
                // Success resolves to an array; failure to {errorMessage} (no `open`, and errorMessage may be
                // undefined). Gate on Array.isArray so the error object never reaches the .map below.
                if (Array.isArray(groups)) {
                    const sortedGroupNames = groups.map((g: any) => g.Name).sort((a: string, b: string) =>
                        a.toLowerCase().localeCompare(b.toLowerCase(), undefined, { numeric: true, sensitivity: 'base' })
                    );
                    setAvailableGroups(sortedGroupNames);
                }
            } catch (err: any) {
                setError({open: true, errorHeader: "Error", errorMessage: err.message});
            }
        };

        if (open) {
            loadGroups();
            setSelectedGroups([]);
            setNewGroupName("");
        }
    }, [open]);

    const handleSubmit = async () => {
        setSubmitting(true);
        const errors: string[] = [];

        try {
            // Create new group if specified
            if (newGroupName && newGroupName.trim()) {
                if (newGroupName.includes(',')) {
                    setError({open: true, errorHeader: "Error", errorMessage: "Group names cannot contain commas"});
                    setSubmitting(false);
                    return;
                }

                const newGroupResult = await utilCreateSupplierGroup(newGroupName.trim(), partnerIds);
                if (newGroupResult.errorMessage) {
                    errors.push(newGroupResult.errorMessage);
                }
            }

            // Add suppliers to selected existing groups
            for (const groupName of selectedGroups) {
                const groups = await utilGetSupplierGroups();
                const group = groups.find((g: any) => g.Name === groupName);
                if (group) {
                    const currentPartnerIds = group.Partners ? group.Partners.map((p: any) => p.Id) : [];
                    const updatedPartnerIds = Array.from(new Set([...currentPartnerIds, ...partnerIds]));
                    
                    if (updatedPartnerIds.length !== currentPartnerIds.length) {
                        const updateResult = await utilUpdateSupplierGroup(groupName, null, updatedPartnerIds);
                        if (updateResult.errorMessage) {
                            errors.push(updateResult.errorMessage);
                        }
                    }
                }
            }

            if (errors.length > 0) {
                setError({open: true, errorHeader: "Error", errorMessage: errors.join(", ")});
            } else {
                setOpen(false);
                setSelectedGroups([]);
                setNewGroupName("");
                if (onGroup) onGroup();
            }
        } catch (err: any) {
            setError({open: true, errorHeader: "Error", errorMessage: err.message});
        }

        setSubmitting(false);
    };

    return (
        <>
            <Popper open={open} anchorEl={anchorEl} transition placement="bottom-start" style={{zIndex: 1300}}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={() => {
                        if (!submitting) {
                            setOpen(false);
                            setSelectedGroups([]);
                            setNewGroupName("");
                        }
                    }}>
                        <Fade {...TransitionProps} timeout={350}>
                            <BulkUpdateStatus container>
                                <Typography>Add suppliers to groups</Typography>

                                <TextField
                                    placeholder="Create new group"
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    error={newGroupName.includes(',')}
                                    disabled={submitting}
                                />

                                <EnhancedAutocomplete
                                    value={selectedGroups}
                                    options={availableGroups.filter(group => !selectedGroups.includes(group))}
                                    onChange={(e, value) => setSelectedGroups(value)}
                                    placeholder={selectedGroups.length > 0 ? "" : "Select existing groups"}
                                    multiple
                                />

                                <Grid item container style={{width: "100%", gap: "8px"}} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            setOpen(false);
                                            setSelectedGroups([]);
                                            setNewGroupName("");
                                        }}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        style={{padding: "6px 12px"}}
                                        disabled={(selectedGroups.length === 0 && !newGroupName.trim()) || submitting}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? <CircularProgress size={16} style={{color: 'white'}}/> : "Save"}
                                    </Button>
                                </Grid>
                            </BulkUpdateStatus>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            <EnhancedErrorDialog
                open={error.open}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
                onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}
            />
        </>
    );
};

export default GroupSupplierPopover;
